import { fetchSiteDashboard } from '@/remote/dashboard/site-dashboard';
import { fetchWaterLevelChart } from '@/remote/dashboard/water-level-chart';
import { QUERY_KEYS } from '@/remote/query-key';
import type { SiteDashboard, WaterLevelChartData } from '@/types/site';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

interface UseSiteDashboardOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchInterval?: number;
  loadAllChartPeriods?: boolean; // 모든 기간의 차트 데이터를 자동으로 로드할지 여부
}

interface UseSiteDashboardReturn {
  // 메인 대시보드 데이터
  data: SiteDashboard | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;

  // 추가 차트 데이터
  chartData: {
    daily: { data: WaterLevelChartData[] | undefined; isLoading: boolean };
    weekly: { data: WaterLevelChartData[] | undefined; isLoading: boolean };
    monthly: { data: WaterLevelChartData[] | undefined; isLoading: boolean };
  };

  actions: {
    refetch: () => void;
    invalidate: () => void;
    invalidateAll: () => void; // 해당 사이트의 모든 대시보드 관련 쿼리 무효화
  };
}

export const useSiteDashboard = (
  siteId: string,
  options: UseSiteDashboardOptions = {}
): UseSiteDashboardReturn => {
  const queryClient = useQueryClient();

  const {
    enabled = true,
    staleTime = 1000 * 30, // 🚀 30초로 단축 - 더 자주 업데이트
    gcTime = 1000 * 60 * 5, // 🚀 5분으로 단축 - 메모리 절약
    refetchInterval = 1000 * 30, // 🚀 30초마다 자동 갱신
    loadAllChartPeriods = false,
  } = options;

  // 메인 대시보드 데이터 쿼리
  const dashboardQuery = useQuery<SiteDashboard>({
    queryKey: QUERY_KEYS.dashboard.siteDashboard(siteId),
    queryFn: () => fetchSiteDashboard(siteId),
    enabled: enabled && !!siteId,
    staleTime,
    gcTime,
    refetchInterval,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // 추가 차트 데이터 쿼리들 (옵션에 따라 활성화)
  const chartQueries = useQueries({
    queries: [
      {
        queryKey: QUERY_KEYS.dashboard.waterLevelChart(siteId, 'daily'),
        queryFn: () => fetchWaterLevelChart(siteId, 'daily'),
        enabled: loadAllChartPeriods && enabled && !!siteId,
        staleTime: staleTime,
        gcTime: gcTime,
      },
      {
        queryKey: QUERY_KEYS.dashboard.waterLevelChart(siteId, 'weekly'),
        queryFn: () => fetchWaterLevelChart(siteId, 'weekly'),
        enabled: loadAllChartPeriods && enabled && !!siteId,
        staleTime: staleTime * 2, // 주간 데이터는 조금 더 긴 staleTime
        gcTime: gcTime,
      },
      {
        queryKey: QUERY_KEYS.dashboard.waterLevelChart(siteId, 'monthly'),
        queryFn: () => fetchWaterLevelChart(siteId, 'monthly'),
        enabled: loadAllChartPeriods && enabled && !!siteId,
        staleTime: staleTime * 5, // 월간 데이터는 더 긴 staleTime
        gcTime: gcTime,
      },
    ],
  });

  // 차트 데이터 정리
  const dailyQuery = chartQueries[0];
  const weeklyQuery = chartQueries[1];
  const monthlyQuery = chartQueries[2];

  const chartData = useMemo(
    () => ({
      daily: {
        data: dailyQuery.data as WaterLevelChartData[] | undefined,
        isLoading: dailyQuery.isLoading,
      },
      weekly: {
        data: weeklyQuery.data as WaterLevelChartData[] | undefined,
        isLoading: weeklyQuery.isLoading,
      },
      monthly: {
        data: monthlyQuery.data as WaterLevelChartData[] | undefined,
        isLoading: monthlyQuery.isLoading,
      },
    }),
    [
      dailyQuery.data,
      dailyQuery.isLoading,
      weeklyQuery.data,
      weeklyQuery.isLoading,
      monthlyQuery.data,
      monthlyQuery.isLoading,
    ]
  );

  const { refetch } = dashboardQuery;

  const refetchAll = useCallback(() => {
    refetch();
    if (loadAllChartPeriods) {
      dailyQuery.refetch();
      weeklyQuery.refetch();
      monthlyQuery.refetch();
    }
  }, [refetch, loadAllChartPeriods, dailyQuery, weeklyQuery, monthlyQuery]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.dashboard.siteDashboard(siteId),
    });
  }, [queryClient, siteId]);

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['대시보드', siteId],
    });
  }, [queryClient, siteId]);

  return {
    data: dashboardQuery.data,
    isLoading: dashboardQuery.isLoading,
    error: dashboardQuery.error,
    isError: dashboardQuery.isError,
    isFetching: dashboardQuery.isFetching,
    isSuccess: dashboardQuery.isSuccess,
    chartData,
    actions: {
      refetch: refetchAll,
      invalidate,
      invalidateAll,
    },
  };
};
